import type { JavascriptSerializable, JavascriptSerializableRecord } from '#layers/data-model/public';
import { formatJavascriptObjectKey, quoteJavascriptString } from './strings';

/**
 * Maximum inline array length before generated snippets switch to multiline formatting.
 */
const INLINE_ARRAY_MAX_LENGTH = 80;

/**
 * Serializes an object as a formatted JavaScript object literal.
 *
 * The output is intended for human-readable snippets, so object records are multiline
 * and arrays stay inline only while they remain compact.
 */
export function serializeJavascriptObject(value: JavascriptSerializableRecord): string {
	return serializeJavascriptValue(value, 0);
}

/**
 * Serializes a supported value into its JavaScript literal representation.
 */
function serializeJavascriptValue(value: JavascriptSerializable, indentLevel: number): string {
	if (Array.isArray(value)) {
		return serializeJavascriptArray(value, indentLevel);
	}

	if (value && typeof value === 'object') {
		return serializeJavascriptRecord(value, indentLevel);
	}

	if (typeof value === 'string') {
		return quoteJavascriptString(value);
	}

	if (typeof value === 'number') {
		return String(value);
	}

	if (typeof value === 'boolean') {
		return value ? 'true' : 'false';
	}

	if (value === undefined) {
		return 'undefined';
	}

	return 'null';
}

/**
 * Serializes arrays inline while short, and expands them once they become multiline or long.
 */
function serializeJavascriptArray(value: JavascriptSerializable[], indentLevel: number): string {
	if (value.length === 0) {
		return '[]';
	}

	const serializedItems = value.map((item) => serializeJavascriptValue(item, indentLevel + 1));
	const inline = `[${serializedItems.join(', ')}]`;

	// Keep compact primitive arrays readable without forcing every field list onto multiple lines.
	if (serializedItems.every((item) => !item.includes('\n')) && inline.length <= INLINE_ARRAY_MAX_LENGTH) {
		return inline;
	}

	const itemIndent = indent(indentLevel + 1);
	const closingIndent = indent(indentLevel);

	return `[\n${serializedItems.map((item) => `${itemIndent}${item},`).join('\n')}\n${closingIndent}]`;
}

/**
 * Serializes object records with stable indentation and trailing commas.
 */
function serializeJavascriptRecord(value: JavascriptSerializableRecord, indentLevel: number): string {
	const entries = Object.entries(value);

	if (entries.length === 0) {
		return '{}';
	}

	const propertyIndent = indent(indentLevel + 1);
	const closingIndent = indent(indentLevel);
	const properties = entries.map(([key, propertyValue]) => {
		return `${propertyIndent}${formatJavascriptObjectKey(key)}: ${serializeJavascriptValue(propertyValue, indentLevel + 1)},`;
	});

	return `{\n${properties.join('\n')}\n${closingIndent}}`;
}

/**
 * Builds the two-space indentation used by generated snippets.
 */
function indent(level: number): string {
	return '  '.repeat(level);
}
